import { FC, useEffect, useState } from 'react';
import CreateEditLayout from '@features/workspace/components/layouts/sub/create-edit-layout';
import { INIT_DATA } from '@features/workspace/components/users/components/user-details-fields';
import history from '@app/services/history';
import LeaveFormSaveDialog from '@features/workspace/components/dialogs/leave-form-save-dialog';
import userSchema from '@features/workspace/components/users/schemas/user-schema';
import { useAppDispatch } from '@store/hooks';
import { setIsLoading } from '@store/features/loading';
import { setNotification } from '@store/features/notification';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import UserForm from '@features/workspace/components/users/form';
import { UserManagementMode } from '@features/workspace/components/users/utils/constants';
import { EmployeePositionEnum, IdentityTypeEnum } from '@utils/constants';
import moment from 'moment/moment';
import { createUserNew } from '@features/workspace/store/actions';
import logger from '@app/services/logger';

const UserCreate: FC<any> = ({
  type = UserManagementMode.Director,
  backUri,
}) => {
  const dispatch = useAppDispatch();
  const [data] = useState<any>(INIT_DATA);
  const [initData] = useState(INIT_DATA);
  const [openLeaveFormSaveDialog, setOpenLeaveFormSaveDialog] = useState(false);

  const { register, handleSubmit, formState, control, watch, setValue } =
    useForm({
      defaultValues: initData,
      resolver: yupResolver(userSchema),
    });

  const { errors, isDirty } = formState;

  logger.debug('errors', errors);
  logger.debug('isDirty', isDirty);

  const observedFormData = watch();

  useEffect(() => {
    logger.debug('Form data changed');
  }, [observedFormData]);

  const onSave = async () => {
    const formData = { ...observedFormData };

    logger.debug('onSave', formData);

    dispatch(setIsLoading(true));

    if (type === UserManagementMode.AuthPerson) {
      formData.position = EmployeePositionEnum.AuthPerson;
    }

    if (formData.person.dateOfBirth) {
      formData.person.dateOfBirth = moment.isMoment(formData.person.dateOfBirth)
        ? formData.person.dateOfBirth.format('YYYY-MM-DD')
        : moment(formData.person.dateOfBirth).format('YYYY-MM-DD');
    }
    logger.debug('calling createUserNew');

    try {
      await dispatch(
        createUserNew({
          userId: formData?.id,
          data: formData,
        })
      ).unwrap();
      dispatch(
        setNotification({
          message: 'User successfully created.',
          variant: 'success',
        })
      );
      goBack();
    } catch (err) {
      logger.error(err);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  useEffect(() => {
    if (
      observedFormData?.person?.identityDocuments?.[0]?.identityType ===
      IdentityTypeEnum.ForeignIdNumber
    ) {
      setValue('person.sisAbsentZAIDReason', null);
    }
  }, [observedFormData?.person?.identityDocuments?.[0]?.identityType]);

  const goBack = () => {
    history.push(backUri);
  };

  const onGoBack = () => {
    if (isDirty) {
      setOpenLeaveFormSaveDialog(true);
    } else {
      goBack();
    }
  };

  return (
    <CreateEditLayout
      backUri={backUri}
      errors={errors}
      onBack={onGoBack}
      onCancel={onGoBack}
      onSave={handleSubmit(onSave)}
    >
      {!!data && (
        <UserForm
          control={control}
          data={observedFormData}
          errors={errors}
          register={register}
          type={type}
        />
      )}
      <LeaveFormSaveDialog
        open={openLeaveFormSaveDialog}
        onAccept={handleSubmit(onSave)}
        onClose={() => setOpenLeaveFormSaveDialog(false)}
        onReject={() => {
          goBack();
        }}
      />
    </CreateEditLayout>
  );
};

export default UserCreate;
